import { useEffect, useMemo, useState } from 'react';
import { Calendar, DateObject } from 'react-multi-date-picker';
import type { CalendarProps, Value } from 'react-multi-date-picker';
import { endOfWeek, startOfWeek } from 'date-fns';

import { Root, Shell, Overlay } from './HistoryCalendarView.styled';
import { Loader, loaderSizes } from '../loader';

export type HistoryCalendarMode = 'single' | 'week' | 'range' | 'year';

export type HistoryCalendarValue =
	| null
	| { mode: 'single'; date: Date }
	| { mode: 'week'; start: Date; end: Date }
	| { mode: 'range'; start: Date | null; end: Date | null }
	| { mode: 'year'; start: Date; end: Date };

type PickerPassThroughProps = Partial<Omit<CalendarProps<boolean, boolean>, 'onChange'>>;

export type HistoryCalendarViewProps = {
	open?: boolean;
	mode: HistoryCalendarMode;
	value: HistoryCalendarValue;
	onChange: (next: HistoryCalendarValue) => void;
	pickerProps?: PickerPassThroughProps;
	loading?: boolean;
};

// eslint-disable-next-line no-magic-numbers
type WeekStartDayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const toDateObject = (d: Date | null | undefined) => (d ? new DateObject(d) : null);

const toJSDate = (v: Value): Date | null => {
	if (!v) return null;
	if (v instanceof DateObject) return v.toDate();
	if (v instanceof Date) return v;

	try {
		return new DateObject(v).toDate();
	} catch {
		return null;
	}
};

export function HistoryCalendarView({
	open = true,
	mode,
	value,
	onChange,
	pickerProps,
	loading = false,
}: HistoryCalendarViewProps) {

	const passThrough = pickerProps ?? {};

	const weekStartsOn = (passThrough.weekStartDayIndex ?? 1) as WeekStartDayIndex;

	const numberOfMonths = mode === 'range' ? 1 : (passThrough.numberOfMonths ?? 1);

	const initialSingle = useMemo<Value>(() => {
		if (value?.mode === 'single') return toDateObject(value.date);
		if (value?.mode === 'week') return toDateObject(value.start);
		if (value?.mode === 'year') return toDateObject(value.start);
		if (value?.mode === 'range' && value.start) return toDateObject(value.start);
		return null;
	}, [value]);

	const initialRange = useMemo<Value[]>(() => {
		if (value?.mode !== 'range') return [null, null];
		return [toDateObject(value.start), toDateObject(value.end)];
	}, [value]);

	const [singleValue, setSingleValue] = useState<Value>(initialSingle);
	const [rangeValues, setRangeValues] = useState<Value[]>(initialRange);

	useEffect(() => setSingleValue(initialSingle), [initialSingle]);
	useEffect(() => setRangeValues(initialRange), [initialRange]);

	const emitSingle = (v: Value) => {
		setSingleValue(v);
		const d = toJSDate(v);
		onChange(d ? { mode: 'single', date: d } : null);
	};

	const emitWeek = (v: Value) => {
		setSingleValue(v);
		const d = toJSDate(v);
		if (!d) return onChange(null);

		const start = startOfWeek(d, { weekStartsOn });
		const end = endOfWeek(d, { weekStartsOn });
		onChange({ mode: 'week', start, end });
	};

	const emitRange = (v: Value[]) => {
		setRangeValues(v);
		const start = toJSDate(v?.[0]);
		const end = toJSDate(v?.[1]);
		onChange({ mode: 'range', start: start ?? null, end: end ?? null });
	};

	const emitYear = (v: Value) => {
		setSingleValue(v);
		const d = toJSDate(v);
		if (!d) return onChange(null);
		const year = d.getFullYear();
		// eslint-disable-next-line no-magic-numbers
		onChange({ mode: 'year', start: new Date(year, 0, 1), end: new Date(year, 11, 31) });
	};

	const handleChange = (next: unknown) => {
		if (mode === 'range') return emitRange(next as Value[]);
		if (mode === 'week') return emitWeek(next as Value);
		if (mode === 'year') return emitYear(next as Value);
		return emitSingle(next as Value);
	};

	return (
		<Root $open={open}>
			<Shell>
				<Calendar
					{...passThrough}
					className="custom-date-range-picker"
					value={mode === 'range' ? rangeValues : singleValue}
					range={mode === 'range'}
					rangeHover={mode === 'range'}
					weekPicker={mode === 'week'}
					onlyYearPicker={mode === 'year'}
					onChange={handleChange}
					weekStartDayIndex={weekStartsOn}
					numberOfMonths={numberOfMonths}
					displayWeekNumbers={false}
				/>

				{loading ? (
					<Overlay>
						<Loader size={loaderSizes.lg} />
					</Overlay>
				) : null}
			</Shell>
		</Root>
	);
}
